/**
 * TraceTemplateModal
 * ------------------
 * Full-screen overlay that lets the operator open an aerial roof
 * screenshot and TRACE the roof outlines on top of it to create a brand
 * new clean line-art roof_design_template.
 *
 * Drawing modes:
 *   • Polygon       — click corners, close on first point / Enter, or
 *                     double-click for an open polyline.
 *   • Straight line — click start, click end, segment auto-commits.
 *                     Perfect for ridges, hips, valleys, downpipes.
 *
 * Drawing aids:
 *   • Endpoint snap  — every committed endpoint becomes a magnetic
 *                      target so subsequent lines join cleanly. Cursor
 *                      shows a green dot when snapping.
 *   • Lock H/V       — forces in-flight lines to be perfectly
 *                      horizontal or vertical (whichever is closer).
 *                      Hold Shift for temporary lock.
 *   • Line typing    — each committed segment can be tagged with a
 *                      type (Ridge / Valley / Gutter / Downpipe / etc).
 *                      Type drives the stroke colour on screen so the
 *                      operator can see the take-off at a glance.
 *   • Length input   — each segment can carry a metric length in m.
 *   • Double-click   — opens an inline editor on any committed shape to
 *                      change its type / length / delete it.
 *
 * Save outputs:
 *   1. Fresh PNG of the line-art on a WHITE background (the aerial is
 *      NOT baked in) → POST `/api/roof-templates/upload`.
 *   2. If `appraisalId` is supplied, every TYPED segment with a length
 *      is ALSO pushed into the appraisal's components via
 *      `POST /api/appraisal-components` so quotes pick up the take-off
 *      automatically.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  X, Pencil, Square, Minus, CircleDot, Undo2, Redo2, Trash2, Save, Loader2, Eye, EyeOff,
  AlertTriangle, Magnet, RectangleHorizontal, Home, Grid3x3,
} from 'lucide-react';
import { toast } from 'sonner';
import { EndpointLayer, ConnectionSummary } from '@/components/trace/EndpointLayer';
import { summariseConnections, nearestGutterPoint, withStrokeIds } from '@/utils/traceConnections';

const MIN_STROKE = 1;
const MAX_STROKE = 8;
const CLOSE_TOLERANCE_PX = 10;   // close-polygon click radius
const SNAP_PX            = 12;   // endpoint-snap radius

// Line types — drives stroke colour AND maps to component_type for the
// optional push-to-components step on save. `pushAs` of null means the
// segment is purely visual (face) and is not pushed.
// `geom` selects which drawing geometry uses each type:
//   - 'line'  → straight-line / polygon segments (linear features)
//   - 'point' → Point mode (single-click circular features)
//
// NOTE: the perimeter of a residential roof IS the gutter run, so
// there is a single combined 'gutter' / Gutter/Perimeter type that
// pushes to Spouting. Old drafts created before this consolidation
// used `lineType: 'outline'` — normaliseLineType() below handles them.
export const LINE_TYPES = [
  // Iter241 — colours mirror PIN_TYPES in annotator/constants.js so
  // newly traced templates render in the same colour as the annotator
  // pin tag and hover highlight (no more orange-ridge vs blue-tag mismatch).
  { key: 'gutter',   label: 'Gutter / Perimeter',  colour: '#15803D', pushAs: 'Spouting', geom: 'line'  },  // green
  { key: 'ridge',    label: 'Ridge',               colour: '#1E40AF', pushAs: 'Ridge',    geom: 'line'  },  // blue
  { key: 'hip',      label: 'Hip',                 colour: '#1E40AF', pushAs: 'Ridge',    geom: 'line'  },  // blue (Hip is a ridge variant)
  { key: 'valley',   label: 'Valley',              colour: '#9F1239', pushAs: 'Valley',   geom: 'line', dash: '6 4' },  // red dotted
  { key: 'barge',    label: 'Barge',               colour: '#A16207', pushAs: 'Barge',    geom: 'line'  },  // brown
  { key: 'downpipe', label: 'Downpipe',            colour: '#EAB308', pushAs: 'Spouting', geom: 'line'  },  // yellow
  { key: 'flashing', label: 'Flashing',            colour: '#111827', pushAs: 'Flashing', geom: 'line'  },  // black
  // Face — dimension line: drawn with double-arrow heads and measured
  // in m² (an area marker, not a length).
  { key: 'face',     label: 'Face',                colour: '#92400E', pushAs: null,       geom: 'line', measure: 'area', arrows: 'double' },  // dark brown
  // Point features — circles, single click, diameter (mm) instead of length (m)
  { key: 'outlet',   label: 'Outlet',              colour: '#EAB308', pushAs: 'Spouting', geom: 'point' },  // yellow
  { key: 'flue',     label: 'Flue',                colour: '#0EA5E9', pushAs: null,       geom: 'point',  // light blue
    flueTypes: ['Flue', 'Gas vent', 'Solid-fuel flue', 'Roof vent'],
    diameters: [80, 100, 125, 150, 175, 200, 250, 300] },
  { key: 'face_m2',  label: 'Face m²',             colour: '#92400E', pushAs: null,       geom: 'point', measure: 'area' },  // area pin (m²) preloaded onto the diagram
  { key: 'pipe',     label: 'Pipe',                colour: '#0EA5E9', pushAs: null,       geom: 'point',  // light blue
    diameters: [25, 32, 40, 50, 65, 80, 100, 150] },
];
// Backward-compat: old drafts saved before the outline→gutter merge
// stored `lineType: 'outline'`. Treat those the same as gutter.
const normaliseLineType = (k) => (k === 'outline' ? 'gutter' : k);
const _t = (key) => LINE_TYPES.find(t => t.key === normaliseLineType(key)) || LINE_TYPES[0];
const typeColour  = (k) => _t(k).colour;
const typeLabel   = (k) => _t(k).label;
const typeGeom    = (k) => _t(k).geom;
const typeDash    = (k) => _t(k).dash || null;
const typeMeasure = (k) => _t(k).measure || 'length';   // 'length' | 'area'
const typeArrows  = (k) => _t(k).arrows || null;        // null | 'double'
const LINE_LINE_TYPES  = LINE_TYPES.filter(t => t.geom === 'line');
const POINT_LINE_TYPES = LINE_TYPES.filter(t => t.geom === 'point');

// Iter182++ — pin-code mapping. Each tagged segment gets a short
// alphabetical pin code (S1, R1, H1, …) so the operator sees the pin
// ID immediately as the line is drawn — same prefix scheme the Master
// Roof Plan annotator uses for its auto-starter pins. Pipe & Flue get
// 2-letter prefixes because they share a starting letter.
const LINE_TO_PIN_PREFIX = {
  gutter:   'Sp',  // Spouting
  ridge:    'R',
  hip:      'H',
  valley:   'V',
  barge:    'B',
  downpipe: 'D',
  flashing: 'F',
  face:     'A',   // Area
  outlet:   'O',
  flue:     'FL',
  pipe:     'P',
};
const pinCodeForIdx = (allStrokes, idx) => {
  const want = normaliseLineType(allStrokes[idx]?.lineType);
  if (!want) return '';
  const prefix = LINE_TO_PIN_PREFIX[want] || want.charAt(0).toUpperCase();
  let n = 0;
  for (let i = 0; i <= idx; i++) {
    if (normaliseLineType(allStrokes[i]?.lineType) === want) n++;
  }
  return `${prefix}${n}`;
};

// ── Per-slot filters for the "Save → assign to ALL slots" feature ──
// Each slot only renders / keeps the shapes whose lineType is in its
// allowlist. `'all'` means no filter (Roof Plan is the master).
const SLOT_FILTERS = {
  roof_plan:        { include: 'all' },
  roofer:           { include: ['gutter','ridge','hip','valley','barge','flashing','flue','pipe'] },
  guttering:        { include: ['gutter','downpipe','outlet'] },
  concrete_restore: { include: ['gutter','ridge','hip','valley','barge','flashing','face','flue','pipe'] },
  painter:          { include: ['gutter','face','flue','pipe','outlet'] },
};

// Roof Diagram slot keys → labels — kept in sync with
// RoofDiagramSlotsCard.js SLOTS array.
const SLOT_LABEL = {
  roof_plan:        'Master Roof Plan',
  roofer:           'Roofing',
  guttering:        'Guttering',
  concrete_restore: 'Concrete Restore',
  painter:          'Painter',
};

export default function TraceTemplateModal({
  open, onClose, aerial, propertyId = '', appraisalId = '', defaultName = '', api, onSaved,
  sourceTemplate = null, saveMode = 'new',
}) {
  // saveMode: 'new' (fresh trace) · 'edit' (save back into sourceTemplate) · 'copy' (new template from sourceTemplate)
  const isEditSave = saveMode === 'edit' && !!sourceTemplate?._id;
  // ── Drawing state ────────────────────────────────────────────────
  // Iter182++ — UX defaults: open in straight-line mode with H/V lock
  // already engaged, since 95% of operator traces are orthogonal roof
  // edges. They can still toggle off via the toolbar.
  const [mode, setMode]       = useState('straight');   // 'polygon' | 'straight' | 'point'
  const [stroke, setStroke]   = useState(2);
  const [strokes, setStrokes] = useState([]);          // committed shapes
  const [activePoly, setActivePoly] = useState(null);  // {points:[]} mid-polygon
  const [activeLineStart, setActiveLineStart] = useState(null);  // {x,y} mid-straight-line
  const [cursor, setCursor]   = useState(null);        // preview-line target (snapped)
  const [bgVisible, setBgVisible] = useState(true);
  const [bgOpacity, setBgOpacity] = useState(60);
  // Iter204 — alignment grid overlay. Helps the tracer line up opposite
  // roof corners + ridges accurately. Cycle Off → Fine (20px) →
  // Coarse (40px) → Off. Persisted to localStorage so the operator
  // doesn't have to re-enable it on every template trace.
  const [gridMode, setGridMode] = useState(() => {
    try { return localStorage.getItem('rtr.trace.grid_mode') || 'off'; }
    catch { return 'off'; }
  });
  useEffect(() => {
    try { localStorage.setItem('rtr.trace.grid_mode', gridMode); } catch { /* ignore */ }
  }, [gridMode]);
  const cycleGrid = () => {
    setGridMode(m => (m === 'off' ? 'fine' : m === 'fine' ? 'coarse' : 'off'));
  };
  const gridStep = gridMode === 'coarse' ? 40 : gridMode === 'fine' ? 20 : 0;
  const [imgDims, setImgDims] = useState({ w: 0, h: 0 });
  const [boxDims, setBoxDims] = useState({ w: 0, h: 0 });
  const [saving, setSaving]   = useState(false);
  const [savedFlash, setSavedFlash] = useState(null);
  // Iter205 — track the _id of any template saved in this modal
  // session. Lets the second save button (e.g. "Save as this
  // property's template" after already clicking "Save as new template")
  // skip the second upload and just LINK the existing template to the
  // property instead of creating a duplicate library entry.
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const [error, setError]     = useState('');
  const [name, setName]       = useState(defaultName || '');

  // Drawing aids
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [snapHit, setSnapHit] = useState(null);   // currently-snapped endpoint, for visual highlight
  // Iter182++ — H/V lock defaults ON so first-time clicks already snap
  // to horizontal / vertical (matches operator habit on orthogonal roofs).
  const [hvLock, setHvLock]   = useState(true);
  const [shiftDown, setShiftDown] = useState(false);

  // Default line-type for new segments. Tracked SEPARATELY for line
  // geometry vs point geometry so switching modes doesn't reset the
  // user's last-pick. Defaults: line='gutter' (the perimeter run IS
  // the gutter), point='outlet' (most common point feature).
  const [defaultLineType,  setDefaultLineType]  = useState('gutter');
  const [defaultPointType, setDefaultPointType] = useState('outlet');

  // "Edit" mode — when on, clicks on existing lines open the editor
  // instead of starting a new drawing. Easier than double-clicking.
  const [editMode, setEditMode] = useState(false);

  // Editor — shape index whose type/length is being edited
  const [editingIdx, setEditingIdx] = useState(null);

  // Iter238 — Copy/paste a line. When the operator right-clicks an
  // existing stroke (or clicks "Copy line" in the edit panel), the
  // tracer enters ghost mode: a translucent duplicate of the stroke
  // follows the cursor, preserving its length and angle. The anchor
  // endpoint snaps to nearby vertices. Keys: R = rotate 15° around
  // the anchor; Tab = swap which endpoint is anchored; Esc/right-click
  // again = cancel; click = commit (pushes a new entry into `strokes`).
  //
  // Shape: { src: stroke object, anchorIdx: 0|1, rotDeg: number }.
  // While active, `editingIdx` is forced to null and normal click
  // routing is bypassed via the `copyGhost` guard at the top of onClick.
  const [copyGhost, setCopyGhost] = useState(null);

  // Redo stack — undone strokes (most-recent first) so the operator
  // can recover from accidental Undo. Any NEW drawing wipes the stack
  // (the standard "branch from current" undo/redo behaviour). Only
  // committed strokes participate — mid-polygon vertices and mid-line
  // starts are transient and excluded.
  const [redoStack, setRedoStack] = useState([]);

  // Target Roof Diagram slot — when set, the saved template is also
  // assigned to that slot on the current appraisal (so each slot ends
  // up showing its own trade-specific drawing rather than all five
  // pointing at the raw aerial).
  const [assignSlot, setAssignSlot] = useState('');

  // ── Draft auto-save (localStorage) ─────────────────────────────
  // Every change to the in-flight strokes is auto-persisted to the
  // browser so the operator never loses their work to a stray Esc, an
  // accidental modal close, or a browser refresh. Keyed per-aerial so
  // multiple drafts can co-exist for different properties.
  const draftKey = aerial?._id ? `rtr.trace.draft.${aerial._id}` : '';
  const [draftSavedAt, setDraftSavedAt] = useState(null);   // unix ms, drives the "Draft saved 14:32" label
  // One-shot restore guard. Once a draft is restored for a given
  // draftKey, we don't restore it again — otherwise hitting Undo back
  // to an empty canvas would silently re-pop the old draft, which then
  // collides with the redo stack.
  const restoredForKeyRef = useRef(null);

  const wrapRef = useRef(null);
  const imgRef  = useRef(null);

  // ── Reset on open ───────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setMode('straight'); setStrokes([]); setActivePoly(null); setActiveLineStart(null);
    setCursor(null); setBgVisible(true); setBgOpacity(60); setSaving(false);
    setSavedFlash(null); setError(''); setSnapEnabled(true); setHvLock(true);
    setSavedTemplateId(null);
    setDefaultLineType('gutter'); setDefaultPointType('outlet');
    setEditingIdx(null); setEditMode(false);
    setAssignSlot('');
    setRedoStack([]);
    setName(defaultName || (aerial?.name ? `Traced — ${aerial.name}` : 'Traced template'));
  }, [open, aerial, defaultName]);

  // Edit / Create-new-from: load the template's own lines + pins so they can be changed.
  const [sourceHasLines, setSourceHasLines] = useState(false);
  useEffect(() => {
    if (!open || !sourceTemplate?._id) return;
    restoredForKeyRef.current = draftKey;  // never let an old local draft override the real template
    let cancelled = false;
    api.get(`/roof-templates/${sourceTemplate._id}`).then(({ data }) => {
      if (cancelled) return;
      const loaded = Array.isArray(data?.strokes) ? data.strokes : [];
      setStrokes(withStrokeIds(loaded));
      const hasLines = loaded.some(s => s?.type !== 'point');
      setSourceHasLines(hasLines);
      if (hasLines) setBgVisible(false);  // the old picture would double up under the editable lines
      setName(saveMode === 'edit' ? (data?.name || '') : `${data?.name || 'Template'} (copy)`);
    }).catch(() => toast.error('Could not load the template lines'));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceTemplate?._id, saveMode]);

  // ── Keyboard handling ───────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        // Iter238 — Esc cancels ghost first; only then close the editor / modal.
        if (copyGhost) { setCopyGhost(null); return; }
        if (editingIdx !== null) setEditingIdx(null);
        else onClose?.();
      } else if (e.key === 'Enter' && activePoly && activePoly.points.length >= 3) {
        commitPolygon(true);
      } else if (e.key === 'Backspace' && activePoly && activePoly.points.length > 0) {
        setActivePoly(p => ({ points: p.points.slice(0, -1) }));
      } else if (e.key === 'Shift') {
        setShiftDown(true);
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault(); undo();
      } else if ((e.metaKey || e.ctrlKey) && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
        e.preventDefault(); redo();
      } else if (copyGhost && (e.key === 'r' || e.key === 'R')) {
        // Iter238 — rotate ghost 15° (Shift+R rotates the other way).
        e.preventDefault();
        const step = e.shiftKey ? -15 : 15;
        setCopyGhost(g => g ? { ...g, rotDeg: ((g.rotDeg || 0) + step) % 360 } : g);
      } else if (copyGhost && e.key === 'Tab') {
        // Iter238 — swap which endpoint is anchored. Useful when the
        // operator wants to attach the OTHER end of the copied line
        // to a snap target.
        e.preventDefault();
        setCopyGhost(g => {
          if (!g) return g;
          const ptsLen = Array.isArray(g.src.points) ? g.src.points.length : 0;
          if (ptsLen < 2) return g;
          return { ...g, anchorIdx: (g.anchorIdx + 1) % ptsLen };
        });
      }
    };
    const onUp = (e) => { if (e.key === 'Shift') setShiftDown(false); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activePoly, activeLineStart, editingIdx, strokes, redoStack, copyGhost]);

  // ── Canvas scale ────────────────────────────────────────────────
  const scale = useMemo(() => {
    if (!imgDims.w || !boxDims.w) return 1;
    return boxDims.w / imgDims.w;
  }, [imgDims, boxDims]);

  // ── Endpoint cache for snap targets ─────────────────────────────
  // Every vertex of every committed shape is a snap target.
  const endpoints = useMemo(() => {
    const arr = [];
    strokes.forEach(s => {
      (s.points || []).forEach(p => arr.push(p));
    });
    if (activePoly) activePoly.points.forEach(p => arr.push(p));
    if (activeLineStart) arr.push(activeLineStart);
    return arr;
  }, [strokes, activePoly, activeLineStart]);

  // ── Coord transforms ────────────────────────────────────────────
  // Convert a mouse event to intrinsic-pixel coords, applying H/V lock
  // (if active and we have an anchor) AND endpoint snap (if enabled).
  // The "anchor" for H/V depends on the current draw context.
  const anchorPoint = () => {
    if (mode === 'straight' && activeLineStart) return activeLineStart;
    if (mode === 'polygon' && activePoly && activePoly.points.length > 0) {
      return activePoly.points[activePoly.points.length - 1];
    }
    return null;
  };

  const rawFromClient = (e) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: (e.clientX - rect.left) / scale, y: (e.clientY - rect.top) / scale };
  };

  // Returns the snapped/constrained intrinsic point AND records which
  // endpoint was snapped (if any) so the SVG layer can draw a halo.
  const adjust = (raw, { allowSnap = true } = {}) => {
    let pt = { ...raw };
    // Step 1 — H/V lock (relative to the in-flight anchor)
    const locking = (hvLock || shiftDown);
    const anchor  = anchorPoint();
    if (locking && anchor) {
      const dx = pt.x - anchor.x;
      const dy = pt.y - anchor.y;
      if (Math.abs(dx) >= Math.abs(dy)) pt = { x: pt.x, y: anchor.y };
      else                              pt = { x: anchor.x, y: pt.y };
    }
    // Step 2 — endpoint snap. Snap wins over H/V (operator intent: if
    // you're aiming at an endpoint, snap to it).
    if (allowSnap && snapEnabled && endpoints.length) {
      let best = null;
      let bestPx = SNAP_PX;
      for (const ep of endpoints) {
        const dpx = Math.hypot((ep.x - raw.x) * scale, (ep.y - raw.y) * scale);
        if (dpx < bestPx) { bestPx = dpx; best = ep; }
      }
      if (best) { pt = { x: best.x, y: best.y }; return { pt, snapHit: best }; }
    }
    return { pt, snapHit: null };
  };

  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

  // ── Iter238 — Copy/paste helpers ────────────────────────────────
  // Resolve the ghost stroke's preview points based on where the
  // anchor endpoint is (cursor or last commit position) plus rotation.
  // Only supports "polygon" type strokes (lines + multi-segments) and
  // "point" type strokes (single dots). Closed polygons (whole roof
  // outlines) are also handled — the entire shape translates as one.
  const computeGhostPoints = (ghost, cursorPt) => {
    if (!ghost || !cursorPt) return null;
    const src = ghost.src;
    const pts = Array.isArray(src.points) ? src.points : [];
    if (pts.length === 0) return null;
    // For "point" strokes, just place a single dot at the cursor.
    if (src.type === 'point') {
      return [{ x: cursorPt.x, y: cursorPt.y }];
    }
    // Anchor = the endpoint that follows the cursor. Other points are
    // rotated/translated relative to the anchor.
    const anchorSrc = pts[ghost.anchorIdx] || pts[0];
    const rad = (ghost.rotDeg || 0) * Math.PI / 180;
    const cosR = Math.cos(rad);
    const sinR = Math.sin(rad);
    return pts.map(p => {
      // Translate so anchor is at origin
      const dx = p.x - anchorSrc.x;
      const dy = p.y - anchorSrc.y;
      // Rotate
      const rx = dx * cosR - dy * sinR;
      const ry = dx * sinR + dy * cosR;
      // Translate so anchor sits at cursor
      return { x: cursorPt.x + rx, y: cursorPt.y + ry };
    });
  };

  const startCopyGhost = (srcIdx) => {
    const src = strokes[srcIdx];
    if (!src) return;
    setEditingIdx(null);
    setActivePoly(null);
    setActiveLineStart(null);
    setCopyGhost({ src: JSON.parse(JSON.stringify(src)), anchorIdx: 0, rotDeg: 0 });
  };

  const commitCopyGhost = (cursorPt) => {
    if (!copyGhost || !cursorPt) return;
    const newPts = computeGhostPoints(copyGhost, cursorPt);
    if (!newPts) { setCopyGhost(null); return; }
    const src = copyGhost.src;
    // New stroke = same shape/lineType/colour/length as source, just
    // with the translated/rotated points. Strip any source-only fields
    // that shouldn't carry over (none today, but future-proof).
    const newStroke = {
      ...src,
      points: newPts,
    };
    setStrokes(s => [...s, newStroke]);
    setRedoStack([]);
    setCopyGhost(null);
  };

  // ── Pointer handlers ────────────────────────────────────────────
  const onPointerMove = (e) => {
    const { pt, snapHit: hit } = adjust(rawFromClient(e));
    setCursor(pt);
    setSnapHit(hit);
  };

  const onClick = (e) => {
    if (saving) return;
    if (justDraggedRef.current) { justDraggedRef.current = false; return; }
    // Iter238 — copy/paste in progress → click commits the ghost at
    // the snapped cursor position (snap logic already ran via adjust()
    // upstream in onPointerMove).
    if (copyGhost) {
      const { pt } = adjust(rawFromClient(e));
      commitCopyGhost(pt);
      return;
    }
    if (editingIdx !== null) return;
    const { pt } = adjust(rawFromClient(e));

    // Edit mode — single click on an existing line/point opens the editor.
    if (editMode) {
      const idx = hitTestShape(pt);
      if (idx !== null) { setEditingIdx(idx); return; }
      return;
    }

    // Point mode — single click drops a circular point feature.
    // After commit, auto-open the editor so the operator can set
    // diameter immediately (a point with no diameter is useless).
    if (mode === 'point') {
      const newIdx = strokes.length;
      let at = pt;
      if (defaultPointType === 'outlet') {
        const g = nearestGutterPoint(strokes, pt);
        if (g && g.d * scale <= SNAP_PX * 1.5) at = g.q;  // outlets sit on their gutter run
      }
      setStrokes(s => [...s, {
        type: 'point', points: [at], stroke,
        lineType: defaultPointType, length_m: null, diameter_mm: null,
      }]);
      setRedoStack([]);
      setEditingIdx(newIdx);
      return;
    }

    // Straight-line mode — single click is ALWAYS treated as a drawing
    // click (start or end of the next line). The snap system makes the
    // click "stick" to any nearby existing endpoint so chained lines
    // join cleanly. Double-click opens the editor for an existing
    // line — see onDoubleClick. To label lines at the end, toggle the
    // explicit Edit mode.
    if (mode === 'straight') {
      if (!activeLineStart) {
        setActiveLineStart(pt);
        return;
      }
      if (dist(pt, activeLineStart) * scale < 2) return;  // ignore zero-length
      // Iter182++ — seed a tiny perpendicular label offset so the chip
      // sits just OFF the line on commit, not directly on top of it.
      // The operator can still drag to fine-tune; Auto-arrange will
      // also re-shuffle later if labels collide.
      const dxL = pt.x - activeLineStart.x;
      const dyL = pt.y - activeLineStart.y;
      const lLen = Math.hypot(dxL, dyL) || 1;
      const seedOffset = 12 / scale;  // ~12 screen px above the line
      const labelDx = -(dyL / lLen) * seedOffset;
      const labelDy =  (dxL / lLen) * seedOffset;
      setStrokes(s => [...s, {
        type: 'polygon', points: [activeLineStart, pt], closed: false, stroke,
        lineType: defaultLineType, length_m: null,
        label_dx: labelDx, label_dy: labelDy,
      }]);
      setRedoStack([]);
      setActiveLineStart(null);
      return;
    }

    // Polygon mode
    if (!activePoly || activePoly.points.length === 0) {
      setActivePoly({ points: [pt] });
      return;
    }
    const first = activePoly.points[0];
    if (dist(pt, first) * scale <= CLOSE_TOLERANCE_PX && activePoly.points.length >= 3) {
      commitPolygon(true);
      return;
    }
    setActivePoly(s => ({ points: [...s.points, pt] }));
  };

  const onDoubleClick = (e) => {
    if (saving) return;
    // In-flight polygon → commit open
    if (mode === 'polygon' && activePoly && activePoly.points.length >= 2) {
      commitPolygon(false);
      return;
    }
    // Otherwise: hit-test on committed shapes to open editor
    const { pt } = adjust(rawFromClient(e), { allowSnap: false });
    const idx = hitTestShape(pt);
    if (idx !== null) setEditingIdx(idx);
  };

  // Distance from point to nearest segment of a shape (intrinsic coords).
  // Points (circles) have their own hit-test that uses the diameter.
  const hitTestShape = (pt) => {
    const HIT_PX = 16;  // generous screen-px tolerance — easier to grab thin lines
    let bestIdx = null, bestPx = HIT_PX;
    strokes.forEach((s, i) => {
      const pts = s.points || [];
      if (s.type === 'point' && pts.length > 0) {
        const r = pointRadiusIntrinsic(s);
        const dpx = Math.hypot(pt.x - pts[0].x, pt.y - pts[0].y) * scale;
        const hit = Math.max(0, dpx - r * scale);
        if (hit < bestPx) { bestPx = hit; bestIdx = i; }
        return;
      }
      if (pts.length < 2) return;
      const last = s.type === 'polygon' && s.closed ? pts.length : pts.length - 1;
      for (let j = 0; j < last; j++) {
        const a = pts[j];
        const b = pts[(j + 1) % pts.length];
        const dpx = pointSegDist(pt, a, b) * scale;
        if (dpx < bestPx) { bestPx = dpx; bestIdx = i; }
      }
    });
    return bestIdx;
  };

  const pointSegDist = (p, a, b) => {
    const dx = b.x - a.x, dy = b.y - a.y;
    if (dx === 0 && dy === 0) return Math.hypot(p.x - a.x, p.y - a.y);
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy)));
    return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
  };

  // Intrinsic-pixel radius for a point shape. Uses the user-set
  // diameter if any, else a visible default. Diameters under 80mm look
  // tiny on a roof aerial — clamp to a minimum visible size on screen.
  const pointRadiusIntrinsic = (s) => {
    const d_mm = Number(s.diameter_mm) || 0;
    if (d_mm > 0) {
      // 1 px ≈ 25 mm (rough heuristic for a typical residential aerial);
      // this is intentionally fuzzy — the diameter is for the supplier,
      // the on-screen size just needs to be readable.
      return Math.max(8, d_mm / 25);
    }
    // No diameter set yet → fixed visible size (~ 14 intrinsic px).
    return 14;
  };

  const commitPolygon = (closed) => {
    if (!activePoly || activePoly.points.length < 2) return;
    // Decompose the in-flight polygon into INDIVIDUAL straight-line
    // segments so the operator can click each side to set its own
    // length / type / measurement. A 4-corner closed perimeter
    // therefore becomes 4 separate clickable lines, not a single
    // multi-segment polygon — exactly what roofers want for take-offs.
    const pts = activePoly.points;
    const last = closed ? pts.length : pts.length - 1;
    // Iter182++ — compute the centroid of all polygon vertices so we
    // can seed every segment's label_dx/dy pointing OUTWARD. For an
    // open chain we fall back to the per-segment perpendicular only;
    // this is good enough either way.
    const cx = pts.reduce((a, p) => a + p.x, 0) / pts.length;
    const cy = pts.reduce((a, p) => a + p.y, 0) / pts.length;
    const seedOffset = 12 / scale;  // ~12 screen px off the line
    const newSegs = [];
    for (let i = 0; i < last; i++) {
      const a = pts[i], b = pts[(i + 1) % pts.length];
      const dxL = b.x - a.x, dyL = b.y - a.y;
      const lLen = Math.hypot(dxL, dyL) || 1;
      let perpX = -dyL / lLen, perpY = dxL / lLen;
      // Push label OUTWARD relative to centroid.
      const mx = (a.x + b.x) / 2 - cx;
      const my = (a.y + b.y) / 2 - cy;
      if (perpX * mx + perpY * my < 0) { perpX = -perpX; perpY = -perpY; }
      newSegs.push({
        type: 'polygon',
        points: [a, b],
        closed: false,
        stroke,
        lineType: defaultLineType,
        length_m: null,
        label_dx: perpX * seedOffset,
        label_dy: perpY * seedOffset,
      });
    }
    setStrokes(s => [...s, ...newSegs]);
    setRedoStack([]);
    setActivePoly(null);
    // Don't auto-open the editor for polygons — a 6-corner perimeter
    // would pop the popover 6 times. The operator can click any
    // segment to label it (or use Edit mode for a sweep).
  };

  // ── Undo / Redo / Clear ─────────────────────────────────────────
  // Re-bound on every render so the keyboard handler always has fresh
  // closures (see the keydown useEffect dependency array). Plain
  // setState calls so React StrictMode's double-run of functional
  // updaters never doubles the side effects.
  // ── Iter182++ — Label auto-arrange ──────────────────────────────
  // Two-stage placement: (1) seed every line label with a small
  // perpendicular offset away from the rough centroid of all line
  // midpoints, so chips sit JUST OFF the line on the outside; (2)
  // iteratively repel chips that overlap each other or sit on top of
  // a point pin / line endpoint.
  //
  // All math runs in intrinsic image coords (not screen px). The
  // visual chip width / height is converted via `scale`.
  const autoArrangeLabels = () => {
    if (strokes.length === 0) return;
    // 1. Compute every line label's geometry
    const labels = strokes.map((s, i) => {
      const pts = s.points || [];
      if (s.type === 'point' || pts.length < 2) {
        // Point pins don't have a draggable chip — return a stub so
        // they still participate as collision blockers.
        return {
          idx: i,
          isPoint: s.type === 'point',
          anchor: pts[0] ? { x: pts[0].x, y: pts[0].y } : { x: 0, y: 0 },
          dx: 0, dy: 0,
          w: 14, h: 14,
          chip: pts[0] ? { x: pts[0].x, y: pts[0].y, w: 14, h: 14 } : null,
        };
      }
      const m = midpoint(pts);
      // Direction of the segment (first → last). For multi-vertex
      // polylines (rare here, segments are 2-point) this still gives
      // a sensible perpendicular.
      const first = pts[0], last = pts[pts.length - 1];
      const lenPx = Math.hypot(last.x - first.x, last.y - first.y) || 1;
      let perpX = -(last.y - first.y) / lenPx;
      let perpY =  (last.x - first.x) / lenPx;
      // Estimate chip dimensions in intrinsic coords.
      const isArea = typeMeasure(s.lineType) === 'area';
      const v = isArea ? s.area_m2 : s.length_m;
      const code = pinCodeForIdx(strokes, i);
      const vTxt = v ? (isArea ? `${Number(v).toFixed(1)} m²` : `${Number(v).toFixed(2)} m`) : '';
      const txt = vTxt ? `${code} · ${vTxt}` : code;
      const wPx = Math.max(28, txt.length * 6.2) + 8;
      const hPx = 17;
      const wInt = wPx / scale;
      const hInt = hPx / scale;
      return {
        idx: i, isPoint: false,
        anchor: { x: m.x, y: m.y },
        perpX, perpY,
        dx: s.label_dx || 0,
        dy: s.label_dy || 0,
        w: wInt, h: hInt,
      };
    });
    // 2. Compute the centroid of all line anchors — used to decide
    //    which side of each line the chip should sit on (outside the
    //    rough shape, not inside it).
    const lineLbls = labels.filter(l => !l.isPoint);
    if (lineLbls.length === 0) return;
    const cx = lineLbls.reduce((a, l) => a + l.anchor.x, 0) / lineLbls.length;
    const cy = lineLbls.reduce((a, l) => a + l.anchor.y, 0) / lineLbls.length;
    // 3. Seed every line label with a perpendicular offset away from
    //    the centroid. Default offset = chip half-height + small gap.
    lineLbls.forEach(l => {
      const vx = l.anchor.x - cx, vy = l.anchor.y - cy;
      // If chip already dragged away from the line, respect it; else
      // pick the outward perpendicular.
      const alreadyOffset = Math.hypot(l.dx, l.dy) > 1 / scale;
      if (alreadyOffset) return;
      let nx = l.perpX, ny = l.perpY;
      if (nx * vx + ny * vy < 0) { nx = -nx; ny = -ny; }
      const off = (l.h * 0.7) + (10 / scale);  // half-chip + 10px gap
      l.dx = nx * off;
      l.dy = ny * off;
    });
    // 4. Build chip bounding boxes from current offsets.
    labels.forEach(l => {
      if (l.isPoint) return;
      l.chip = {
        x: l.anchor.x + l.dx - l.w / 2,
        y: l.anchor.y + l.dy - l.h / 2,
        w: l.w, h: l.h,
      };
    });
    // 5. Iteratively repel any overlapping chips. Cap at 24 passes —
    //    convergence is usually < 8 iterations for typical roofs.
    const rectOverlap = (a, b) => {
      if (!a || !b) return false;
      const padX = 2 / scale, padY = 2 / scale;
      return !(a.x + a.w + padX < b.x
            || b.x + b.w + padX < a.x
            || a.y + a.h + padY < b.y
            || b.y + b.h + padY < a.y);
    };
    for (let pass = 0; pass < 24; pass++) {
      let nudged = false;
      for (let i = 0; i < labels.length; i++) {
        const A = labels[i];
        if (!A.chip) continue;
        for (let j = i + 1; j < labels.length; j++) {
          const B = labels[j];
          if (!B.chip) continue;
          if (!rectOverlap(A.chip, B.chip)) continue;
          // Push the LINE labels away from each other along the
          // connecting vector. Point pins don't move.
          const acx = A.chip.x + A.w / 2;
          const acy = A.chip.y + A.h / 2;
          const bcx = B.chip.x + B.w / 2;
          const bcy = B.chip.y + B.h / 2;
          let vx = acx - bcx, vy = acy - bcy;
          if (Math.hypot(vx, vy) < 0.0001) {
            // Centres coincide — nudge along arbitrary axis.
            vx = 1; vy = 0;
          }
          const mag = Math.hypot(vx, vy);
          vx /= mag; vy /= mag;
          // Overlap depth on each axis → step ~ half the deeper overlap.
          const ox = Math.min(A.chip.x + A.w, B.chip.x + B.w) - Math.max(A.chip.x, B.chip.x);
          const oy = Math.min(A.chip.y + A.h, B.chip.y + B.h) - Math.max(A.chip.y, B.chip.y);
          const step = Math.max(ox, oy) * 0.55 + 1 / scale;
          if (!A.isPoint) { A.dx += vx * step; A.dy += vy * step; }
          if (!B.isPoint) { B.dx -= vx * step; B.dy -= vy * step; }
          // Recompute moved chips
          if (!A.isPoint) A.chip = { x: A.anchor.x + A.dx - A.w / 2, y: A.anchor.y + A.dy - A.h / 2, w: A.w, h: A.h };
          if (!B.isPoint) B.chip = { x: B.anchor.x + B.dx - B.w / 2, y: B.anchor.y + B.dy - B.h / 2, w: B.w, h: B.h };
          nudged = true;
        }
      }
      if (!nudged) break;
    }
    // 6. Write the new offsets back onto the strokes.
    setStrokes(arr => arr.map((s, i) => {
      const L = labels.find(x => x.idx === i);
      if (!L || L.isPoint) return s;
      return { ...s, label_dx: L.dx, label_dy: L.dy };
    }));
  };

  const undo = () => {
    if (moveStackRef.current.length && moveStackRef.current[moveStackRef.current.length - 1].len === strokes.length) {
      moveRedoRef.current.push(strokes);
      setStrokes(moveStackRef.current.pop().before);
      return;
    }
    if (activePoly && activePoly.points.length > 0) {
      const next = activePoly.points.slice(0, -1);
      setActivePoly(next.length ? { points: next } : null);
      return;
    }
    if (activeLineStart) { setActiveLineStart(null); return; }
    if (strokes.length === 0) return;
    const popped = strokes[strokes.length - 1];
    setStrokes(strokes.slice(0, -1));
    setRedoStack([popped, ...redoStack]);
  };
  const redo = () => {
    if (moveRedoRef.current.length) {
      moveStackRef.current.push({ before: strokes, len: strokes.length });
      setStrokes(moveRedoRef.current.pop());
      return;
    }
    if (redoStack.length === 0) return;
    const [next, ...rest] = redoStack;
    setStrokes([...strokes, next]);
    setRedoStack(rest);
  };
  const clearAll = () => {
    setStrokes([]); setActivePoly(null); setActiveLineStart(null);
    setRedoStack([]);
  };

  // ── Update / delete a committed shape ───────────────────────────
  // ── Drag a line end (Edit mode). Joined ends move together when enabled.
  const moveStackRef = useRef([]);
  const moveRedoRef = useRef([]);
  const justDraggedRef = useRef(false);
  const [moveJoined, setMoveJoined] = useState(true);
  const connTol = 8 / (scale || 1);
  const connections = useMemo(() => summariseConnections(strokes, connTol), [strokes, connTol]);
  const startEndpointDrag = (si, pi) => {
    const before = strokes;
    const origin = before[si].points[pi];
    const node = connections.nodes.find(n => n.refs.some(r => r.si === si && r.pi === pi));
    const refs = moveJoined && node ? node.refs : [{ si, pi }];
    const neighbour = before[si].points[pi > 0 ? pi - 1 : pi + 1];
    const moving = new Set(refs.map(r => `${r.si}:${r.pi}`));
    const others = [];
    before.forEach((s, i) => (s.points || []).forEach((p, j) => { if (!moving.has(`${i}:${j}`)) others.push(p); }));
    let moved = false;
    const onMove = (ev) => {
      if (!moved) { moved = true; moveStackRef.current.push({ before, len: before.length }); moveRedoRef.current = []; }
      let p = rawFromClient(ev);
      if ((hvLock || ev.shiftKey) && neighbour) {
        p = Math.abs(p.x - neighbour.x) >= Math.abs(p.y - neighbour.y) ? { x: p.x, y: neighbour.y } : { x: neighbour.x, y: p.y };
      }
      if (snapEnabled) {
        const hit = others.find(o => Math.hypot((o.x - p.x) * scale, (o.y - p.y) * scale) < SNAP_PX);
        if (hit) p = { x: hit.x, y: hit.y };
      }
      setStrokes(cur => cur.map((s, i) => (refs.some(r => r.si === i)
        ? { ...s, points: s.points.map((q, j) => (moving.has(`${i}:${j}`) ? { ...q, x: p.x, y: p.y } : q)) }
        : s)));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      if (moved) { justDraggedRef.current = true; setTimeout(() => { justDraggedRef.current = false; }, 0); setRedoStack([]); }
    };
    if (!origin) return;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // ── Update / delete a committed shape ───────────────────────────
  // Any direct mutation of strokes (patch / remove / draw) wipes the
  // redo stack so it never reapplies onto a different branch of history.
  const patchShape = (idx, patch) => {
    setStrokes(s => s.map((x, i) => i === idx ? { ...x, ...patch } : x));
    setRedoStack([]);
  };
  const removeShape = (idx) => {
    setStrokes(s => s.filter((_, i) => i !== idx));
    setRedoStack([]);
  };

  // ── Save ────────────────────────────────────────────────────────
  // Renders a subset of strokes onto a fresh white canvas. When
  // `filter` is undefined or 'all', renders everything; otherwise
  // `filter` is an array of lineType keys that ARE included. The
  // aerial photo is NEVER baked in — output is always clean line-art.
  const renderToBlob = (filter) => new Promise((resolve, reject) => {
    try {
      const W = imgDims.w || 1200;
      const H = imgDims.h || 900;
      const canvas = document.createElement('canvas');
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, W, H);
      ctx.lineJoin = 'round';
      ctx.lineCap  = 'round';
      const passFilter = (lt) => {
        if (!filter || filter === 'all') return true;
        return filter.includes(normaliseLineType(lt));
      };
      // Helper — draw a single arrowhead at point p, pointing FROM
      // direction (dx,dy). Used for the Face double-arrow dimension line.
      const drawArrow = (p, dx, dy, colour, w) => {
        const len = Math.hypot(dx, dy) || 1;
        const ux = dx / len, uy = dy / len;
        const size = Math.max(8, w * 3);
        const ax = p.x - ux * size;
        const ay = p.y - uy * size;
        const px = -uy * (size * 0.6);
        const py =  ux * (size * 0.6);
        ctx.fillStyle = colour;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(ax + px, ay + py);
        ctx.lineTo(ax - px, ay - py);
        ctx.closePath();
        ctx.fill();
      };
      strokes.forEach((s) => {
        if (!passFilter(s.lineType)) return;
        const colour = typeColour(s.lineType);
        // Point feature → circle (Outlet / Flue / Pipe)
        if (s.type === 'point' && s.points?.length > 0) {
          const r = pointRadiusIntrinsic(s);
          ctx.strokeStyle = colour;
          ctx.fillStyle   = '#FFFFFF';
          ctx.lineWidth   = (s.stroke || stroke) * 2;
          ctx.setLineDash([]);
          ctx.beginPath();
          ctx.arc(s.points[0].x, s.points[0].y, r, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          if (s.diameter_mm || s.area_m2) {
            ctx.fillStyle = colour;
            ctx.font = 'bold 12px monospace';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            const tag = s.area_m2 ? `${Number(s.area_m2).toFixed(1)} m²` : s.flue_type ? `${s.flue_type} DN${s.diameter_mm}` : `DN${s.diameter_mm}`;
            ctx.fillText(tag, s.points[0].x, s.points[0].y + r + 12);
          }
          return;
        }
        // Linear feature
        if (!s.points || s.points.length < 2) return;
        ctx.strokeStyle = colour;
        const w = (s.stroke || stroke) * 2;
        ctx.lineWidth = w;
        const dash = typeDash(s.lineType);
        if (dash) ctx.setLineDash(dash.split(' ').map(n => parseFloat(n) * w));
        else      ctx.setLineDash([]);
        ctx.beginPath();
        s.points.forEach((p, i) => { if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y); });
        if (s.type === 'polygon' && s.closed) ctx.closePath();
        ctx.stroke();
        ctx.setLineDash([]);

        // Double-arrow ends for Face dimension lines
        if (typeArrows(s.lineType) === 'double' && s.points.length >= 2) {
          const p0 = s.points[0];
          const p1 = s.points[1];
          drawArrow(p0, p0.x - p1.x, p0.y - p1.y, colour, s.stroke || stroke);
          drawArrow(p1, p1.x - p0.x, p1.y - p0.y, colour, s.stroke || stroke);
        }

        // Measurement label (length OR area depending on the line type)
        const isArea = typeMeasure(s.lineType) === 'area';
        const v = isArea ? s.area_m2 : s.length_m;
        if (v) {
          const m = midpoint(s.points);
          if (m) {
            const text = isArea ? `${Number(v).toFixed(1)} m²` : `${Number(v).toFixed(2)}m`;
            ctx.font = 'bold 14px ui-sans-serif, system-ui, sans-serif';
            const tw = ctx.measureText(text).width;
            const lblW = Math.max(50, tw) + 10;
            ctx.fillStyle = '#FFFFFF';
            ctx.strokeStyle = colour;
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.rect(m.x - lblW / 2, m.y - 11, lblW, 22);
            ctx.fill();
            ctx.stroke();
            ctx.fillStyle = colour;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(text, m.x, m.y);
          }
        }
      });
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Canvas export failed')), 'image/png');
    } catch (e) { reject(e); }
  });

  const pushTypedSegmentsToComponents = async () => {
    if (!appraisalId) return { pushed: 0 };
    let pushed = 0;
    for (const s of strokes) {
      const lt = LINE_TYPES.find(t => t.key === normaliseLineType(s.lineType));
      if (!lt || !lt.pushAs) continue;
      // Linear feature with a length
      if (s.type === 'polygon') {
        const length_m = Number(s.length_m) || 0;
        if (length_m <= 0) continue;
        try {
          await api.post('/appraisal-components', {
            appraisal_id: appraisalId,
            component_type: lt.pushAs,
            item_type: lt.label,
            quantity: 1,
            length: length_m,
            requirements: 'Auto-imported from Trace tool',
            source: 'diagram',
          });
          pushed += 1;
        } catch (e) { console.warn('[Trace push] line failed', e); }
        continue;
      }
      // Point feature (Outlet etc) — push as quantity-1 with diameter
      // captured in the requirements note for the supplier.
      if (s.type === 'point') {
        const diameter_mm = Number(s.diameter_mm) || 0;
        try {
          await api.post('/appraisal-components', {
            appraisal_id: appraisalId,
            component_type: lt.pushAs,
            item_type: lt.label,
            quantity: 1,
            length: 0,
            requirements: `Auto-imported from Trace tool${diameter_mm ? ` · DN${diameter_mm}` : ''}`,
            source: 'diagram',
          });
          pushed += 1;
        } catch (e) { console.warn('[Trace push] point failed', e); }
      }
    }
    return { pushed };
  };

  // ── Save as this Property's Main Template ─────────────────────
  // Iter182++ — convenience save that uploads the new template AND
  // assigns it as the property's `roof_template_id` so it shows up
  // pre-selected on every appraisal for this property. Same final
  // result as: "Save as new template" → go to /roof-templates →
  // "Accept as Main Roof Template" — collapsed to one button.
  const saveAsPropertyTemplate = async () => {
    setError('');
    if (strokes.length === 0) { setError('Draw at least one line before saving.'); return; }
    if (!name.trim())          { setError('Give the new template a name.'); return; }
    if (!propertyId)           { setError('No property context — use "Save as new template" instead.'); return; }
    setSaving(true);
    try {
      let templateId = savedTemplateId;
      let templateName = name.trim();
      // Iter205 — if the operator already clicked "Save as new template"
      // in this modal session, don't re-upload. Just link the existing
      // template to the property — prevents duplicate library entries.
      if (!templateId) {
        const blob = await renderToBlob();
        const file = new File([blob], `${templateName}.png`, { type: 'image/png' });
        const fd = new FormData();
        fd.append('file', file);
        fd.append('name', templateName);
        fd.append('category', 'Traced Outline');
        fd.append('property_id', propertyId);
        try { fd.append('strokes', JSON.stringify(strokes)); } catch {} // eslint-disable-line no-empty
        const { data } = await api.post('/roof-templates/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        templateId = data._id;
        templateName = data.name;
        setSavedTemplateId(templateId);
      }
      // Mark the template as the property's main roof template.
      try {
        await api.patch(`/properties/${propertyId}`, { roof_template_id: templateId });
      } catch (e) {
        setError(`Saved "${templateName}" but couldn't link to the property — open the template detail page to accept it manually.`);
        setSaving(false);
        return;
      }
      // Iter182++ — Also push the new template onto the CURRENT appraisal's
      // Master Roof Plan slot (if we have one in context). Without this,
      // the appraisal's `roof_plan_template_id` stays empty and the
      // Master Roof Plan editor opens with no template attached, even
      // though the property itself is linked. Best-effort only — a
      // failure here doesn't undo the successful property link.
      if (appraisalId) {
        try {
          await api.patch(`/appraisals/${appraisalId}/roof-template`, { roof_plan_template_id: templateId });
        } catch (e) {
          // Non-fatal — surface as toast info, not an error.
          console.warn('[trace] property template saved but appraisal slot patch failed:', e);
        }
      }
      setSavedFlash({ ok: true, msg: `"${templateName}" is now this property's main template.` });
      onSaved?.({ _id: templateId, name: templateName });
      try { if (draftKey) localStorage.removeItem(draftKey); } catch {} // eslint-disable-line no-empty
      setDraftSavedAt(null);
      toast.success(`"${templateName}" is now the main template for this property.`);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setError('');
    if (strokes.length === 0) { setError('Draw at least one line before saving.'); return; }
    if (!name.trim())          { setError('Give the new template a name.'); return; }
    // Iter205 — if we already saved this trace in the current modal
    // session, refuse the duplicate POST. The Save buttons are now
    // re-purposed as "✓ Saved" once a save has happened.
    if (savedTemplateId && !isEditSave) {
      setError('This trace is already saved as a template. Click "Save as this property\'s template" if you also want to make it the property default.');
      return;
    }
    setSaving(true);
    try {
      const blob = await renderToBlob();
      const file = new File([blob], `${name.trim()}.png`, { type: 'image/png' });
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', name.trim());
      if (isEditSave) {
        const { data: usage } = await api.get(`/roof-templates/${sourceTemplate._id}/usage`);
        if (usage?.count > 0 && !window.confirm(`This template is used on ${usage.count} job${usage.count === 1 ? '' : 's'}. Their diagrams will show the new drawing; pins on lines you moved or deleted may need re-checking (see "Used on jobs"). Save anyway?`)) {
          setSaving(false);
          return;
        }
        fd.append('strokes', JSON.stringify(withStrokeIds(strokes)));
        const { data } = await api.put(`/roof-templates/${sourceTemplate._id}/drawing`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setSavedFlash({ ok: true, msg: `Saved changes to "${data.name}".` });
        setSavedTemplateId(data._id);
        onSaved?.(data);
        try { if (draftKey) localStorage.removeItem(draftKey); } catch {} // eslint-disable-line no-empty
        setDraftSavedAt(null);
        return;
      }
      fd.append('category', 'Traced Outline');
      if (propertyId) fd.append('property_id', propertyId);
      // Iter182 — round-trip the vector geometry so the annotator can
      // render clickable per-segment hot-spots on the saved template.
      try { fd.append('strokes', JSON.stringify(withStrokeIds(strokes))); } catch {} // eslint-disable-line no-empty
      const { data } = await api.post('/roof-templates/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Optionally assign the brand-new template to a specific Roof
      // Diagram slot on this appraisal so each slot ends up showing
      // its own trade-specific drawing (rather than all five pointing
      // at the same aerial).
      let assignedTail = '';
      if (appraisalId && assignSlot) {
        const fieldByKey = {
          roof_plan:        'roof_plan_template_id',
          roofer:           'roofer_measurements_template_id',
          guttering:        'guttering_measurements_template_id',
          concrete_restore: 'concrete_restore_measurements_template_id',
          painter:          'painter_measurements_template_id',
        };
        const field = fieldByKey[assignSlot];
        if (field) {
          try {
            await api.patch(`/appraisals/${appraisalId}/roof-template`, { [field]: data._id });
            assignedTail = ` · assigned to the ${SLOT_LABEL[assignSlot]} slot`;
          } catch (e) {
            console.warn('[Trace] slot assign failed', e);
            assignedTail = ` · (couldn't auto-assign to ${SLOT_LABEL[assignSlot]} slot — pick it manually)`;
          }
        }
      }
      const { pushed } = await pushTypedSegmentsToComponents();
      const componentTail = pushed > 0 ? ` · pushed ${pushed} component${pushed === 1 ? '' : 's'} into this appraisal` : '';
      setSavedFlash({ ok: true, msg: `Saved "${data.name}"${assignedTail}${componentTail}.` });
      setSavedTemplateId(data._id);
      onSaved?.(data);
      try { if (draftKey) localStorage.removeItem(draftKey); } catch {} // eslint-disable-line no-empty
      setDraftSavedAt(null);
      if (pushed > 0) toast.success(`${pushed} typed segment${pushed === 1 ? '' : 's'} added to Components.`);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Multi-slot save ────────────────────────────────────────────
  // Renders 5 trade-specific PNGs from the SAME tracing — each filtered
  // to only the lines / points relevant to that trade — uploads each as
  // its own roof_design_template, and assigns each to the matching
  // Roof Diagram slot on the appraisal. One click = all 5 slots
  // populated with their own bespoke drawing.
  const saveToAllSlots = async () => {
    setError('');
    if (strokes.length === 0) { setError('Draw at least one line before saving.'); return; }
    if (!name.trim())          { setError('Give the new template a name.'); return; }
    if (!appraisalId)          { setError('Multi-slot save needs an appraisal context.'); return; }
    setSaving(true);
    const slots = [
      { key: 'roof_plan',        field: 'roof_plan_template_id',                     suffix: 'Roof Plan' },
      { key: 'roofer',           field: 'roofer_measurements_template_id',           suffix: 'Roofing' },
      { key: 'guttering',        field: 'guttering_measurements_template_id',        suffix: 'Guttering' },
      { key: 'concrete_restore', field: 'concrete_restore_measurements_template_id', suffix: 'Concrete Restore' },
      { key: 'painter',          field: 'painter_measurements_template_id',          suffix: 'Painter' },
    ];
    let okSlots = 0; let firstId = null;
    const update = {};
    try {
      for (const slot of slots) {
        const filter = SLOT_FILTERS[slot.key]?.include;
        const hasAny = filter === 'all' || strokes.some(s => filter.includes(normaliseLineType(s.lineType)));
        if (!hasAny) continue;
        const blob = await renderToBlob(filter);
        const file = new File([blob], `${name.trim()} — ${slot.suffix}.png`, { type: 'image/png' });
        const fd = new FormData();
        fd.append('file', file);
        fd.append('name', `${name.trim()} — ${slot.suffix}`);
        fd.append('category', 'Traced Outline');
        if (propertyId) fd.append('property_id', propertyId);
        // Iter182 — round-trip the (filtered) vector geometry per slot
        // so each saved template can power click-to-pin in the
        // annotator with only the trade-relevant segments.
        try {
          const filteredStrokes = filter === 'all'
            ? strokes
            : strokes.filter(s => filter.includes(normaliseLineType(s.lineType)));
          fd.append('strokes', JSON.stringify(filteredStrokes));
        } catch {} // eslint-disable-line no-empty
        const { data } = await api.post('/roof-templates/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        update[slot.field] = data._id;
        firstId = firstId || data._id;
        okSlots += 1;
      }
      if (okSlots > 0) {
        await api.patch(`/appraisals/${appraisalId}/roof-template`, update);
      }
      const { pushed } = await pushTypedSegmentsToComponents();
      const componentTail = pushed > 0 ? ` · ${pushed} component${pushed === 1 ? '' : 's'} added` : '';
      setSavedFlash({ ok: true, msg: `Saved ${okSlots} trade-specific template${okSlots === 1 ? '' : 's'} — all slots updated.${componentTail}` });
      onSaved?.({ _id: firstId, name: name.trim() });
      try { if (draftKey) localStorage.removeItem(draftKey); } catch {} // eslint-disable-line no-empty
      setDraftSavedAt(null);
      if (pushed > 0) toast.success(`${pushed} typed segment${pushed === 1 ? '' : 's'} added to Components.`);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Multi-slot save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Image sizing ────────────────────────────────────────────────
  const onImgLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    setImgDims({ w: img.naturalWidth, h: img.naturalHeight });
    setBoxDims({ w: img.clientWidth, h: img.clientHeight });
  };
  useEffect(() => {
    if (!open) return;
    const onResize = () => {
      const img = imgRef.current;
      if (img) setBoxDims({ w: img.clientWidth, h: img.clientHeight });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);

  // ── Slot suggestion ────────────────────────────────────────────
  // Look at what types of lines/points the operator has drawn and
  // suggest which Roof Diagram slot the new template should land in.
  // Heuristic: count shapes by trade family, pick the dominant.
  // NOTE: these hooks must live ABOVE the early-return below — React
  // requires hooks to be called in the same order on every render.
  const suggestedSlot = useMemo(() => {
    if (!appraisalId || strokes.length === 0) return '';
    const buckets = { roofer: 0, guttering: 0, concrete_restore: 0, painter: 0, roof_plan: 0 };
    for (const s of strokes) {
      switch (normaliseLineType(s.lineType)) {
        case 'ridge': case 'hip': case 'valley': case 'barge': case 'flashing':
          buckets.roofer += 1; buckets.concrete_restore += 1; break;
        case 'gutter': case 'downpipe': case 'outlet':
          // A bare perimeter trace is a Roof Plan (small weight); add
          // downpipes or outlets and the dominant intent flips to
          // Guttering.
          buckets.guttering += 1; buckets.roof_plan += 0.4; break;
        case 'face':
          buckets.concrete_restore += 1; buckets.painter += 1; break;
        case 'flue': case 'pipe':
          buckets.roofer += 0.5; break;
        default: break;
      }
    }
    let bestKey = ''; let bestVal = 0;
    for (const [k, v] of Object.entries(buckets)) {
      if (v > bestVal) { bestVal = v; bestKey = k; }
    }
    if (bestVal === 0) return 'roof_plan';
    return bestKey;
  }, [appraisalId, strokes]);

  useEffect(() => {
    if (!appraisalId) return;
    if (assignSlot === '' && suggestedSlot) {
      setAssignSlot(suggestedSlot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedSlot, appraisalId]);

  // ── Restore any saved draft on open ─────────────────────────────
  // One-shot per (open × draftKey): the ref guard stops a re-restore
  // when the user manually Clears or Undos back to an empty canvas.
  useEffect(() => {
    if (!open || !draftKey) return;
    if (restoredForKeyRef.current === draftKey) return;
    if (strokes.length > 0) {
      // Operator already has content — mark as "already handled" so
      // we don't re-restore later.
      restoredForKeyRef.current = draftKey;
      return;
    }
    try {
      const raw = localStorage.getItem(draftKey);
      restoredForKeyRef.current = draftKey;   // mark first to prevent re-entry
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft?.strokes?.length > 0) {
        setStrokes(draft.strokes);
        if (draft.name) setName(draft.name);
        if (draft.assignSlot) setAssignSlot(draft.assignSlot);
        setDraftSavedAt(draft.savedAt || Date.now());
        toast.success(`Restored draft with ${draft.strokes.length} shape${draft.strokes.length === 1 ? '' : 's'} from your last session.`);
      }
    } catch (e) {
      console.warn('[Trace] draft restore failed', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draftKey, strokes.length]);

  // Clear the one-shot guard when modal closes so the next session
  // can restore again.
  useEffect(() => {
    if (!open) restoredForKeyRef.current = null;
  }, [open]);

  // ── Auto-save the draft on every change ─────────────────────────
  // 300 ms debounce so a rapid sequence of clicks (e.g. tracing a 10-
  // corner perimeter) doesn't pound localStorage. Only writes when the
  // canvas has at least one stroke — name-only edits never overwrite
  // a draft with empty strokes. Cleared when the user successfully
  // saves the final template.
  useEffect(() => {
    if (!open || !draftKey) return;
    if (strokes.length === 0) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify({
          strokes, name, assignSlot,
          aerialId: aerial?._id,
          savedAt: Date.now(),
        }));
        setDraftSavedAt(Date.now());
      } catch (e) {
        console.warn('[Trace] draft save failed', e);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strokes, name, assignSlot, open, draftKey]);

  const discardDraft = () => {
    if (!draftKey) return;
    if (strokes.length > 0 && !window.confirm('Discard ALL current strokes and the auto-saved draft for this aerial? Cannot be undone.')) return;
    try { localStorage.removeItem(draftKey); } catch {} // eslint-disable-line no-empty
    setStrokes([]); setActivePoly(null); setActiveLineStart(null);
    setDraftSavedAt(null);
    toast.success('Draft discarded.');
  };

  if (!open || !aerial) return null;

  const imageUrl = aerial._id ? `/api/roof-templates/${aerial._id}/image` : aerial.thumbnail_url;
  const path = (pts, closed) => {
    if (!pts || pts.length === 0) return '';
    const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x * scale} ${p.y * scale}`).join(' ');
    return closed ? `${d} Z` : d;
  };

  // Midpoint of a polyline (for hover badges + editor anchoring)
  const midpoint = (pts) => {
    if (!pts || pts.length < 2) return null;
    const i = Math.floor(pts.length / 2);
    if (pts.length % 2 === 0) {
      return { x: (pts[i - 1].x + pts[i].x) / 2, y: (pts[i - 1].y + pts[i].y) / 2 };
    }
    return pts[i];
  };

  const editingShape = editingIdx !== null ? strokes[editingIdx] : null;
  const editingAnchor = editingShape
    ? (editingShape.type === 'point' ? editingShape.points?.[0] : midpoint(editingShape.points))
    : null;

  return (
    <div
      data-testid="trace-template-modal"
      className="fixed inset-0 z-[60] bg-black/75 flex items-center justify-center p-3"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl flex flex-col" style={{ maxHeight: '96vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200" style={{ borderTop: '4px solid #D35400' }}>
          <div>
            <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <Pencil size={14} className="text-[#D35400]" /> {saveMode === 'edit' ? 'Edit roof template' : saveMode === 'copy' ? 'Create new template from this one' : 'Trace new roof template'}
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Drawing on <strong>{aerial.name || 'aerial'}</strong>. Saved output is clean line-art (aerial NOT baked in).
              {appraisalId && <span className="ml-1 text-emerald-600 font-semibold">Typed segments will push to this appraisal's components.</span>}
            </p>
          </div>
          <button data-testid="trace-close" onClick={onClose} className="p-1.5 rounded hover:bg-slate-100" title="Close (Esc)"><X size={16} /></button>
        </div>

        {/* Toolbar — row 1: mode + stroke + bg */}
        <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-100 bg-slate-50 flex-wrap">
          <div className="inline-flex rounded-md overflow-hidden border border-slate-300">
            <button
              data-testid="trace-mode-polygon"
              onClick={() => { setMode('polygon'); setActiveLineStart(null); setEditMode(false); }}
              className={`flex items-center gap-1 text-[11px] px-2.5 py-1 font-semibold ${mode === 'polygon' && !editMode ? 'bg-[#D35400] text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
              title="Polygon mode — click corners, click the first point (or Enter) to close. Double-click commits open."
            ><Square size={11} /> Polygon</button>
            <button
              data-testid="trace-mode-straight"
              onClick={() => { setMode('straight'); setActivePoly(null); setEditMode(false); }}
              className={`flex items-center gap-1 text-[11px] px-2.5 py-1 font-semibold border-l border-slate-300 ${mode === 'straight' && !editMode ? 'bg-[#D35400] text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
              title="Straight line — click start, click end. Auto-commits."
            ><Minus size={11} /> Straight line</button>
            <button
              data-testid="trace-mode-point"
              onClick={() => { setMode('point'); setActivePoly(null); setActiveLineStart(null); setEditMode(false); }}
              className={`flex items-center gap-1 text-[11px] px-2.5 py-1 font-semibold border-l border-slate-300 ${mode === 'point' && !editMode ? 'bg-[#D35400] text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
              title="Point mode — drop a circle for Outlets, Flues, Pipes. Diameter (mm) is set per-point."
            ><CircleDot size={11} /> Point</button>
            <button
              data-testid="trace-mode-edit"
              onClick={() => { setEditMode(v => !v); setActiveLineStart(null); setActivePoly(null); }}
              className={`flex items-center gap-1 text-[11px] px-2.5 py-1 font-semibold border-l border-slate-300 ${editMode ? 'bg-sky-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
              title="Edit mode — drag the square handles to move line ends; click a line or point to set its type / length / diameter."
            ><Pencil size={11} /> Edit</button>
          </div>
          {editMode && (
            <label className="flex items-center gap-1 text-[11px] text-slate-600" title="When you drag a corner, every line that meets there moves with it">
              <input type="checkbox" data-testid="trace-move-joined" checked={moveJoined} onChange={e => setMoveJoined(e.target.checked)} />
              Move joined ends together
            </label>
          )}
          {sourceTemplate?._id && !sourceHasLines && (
            <span data-testid="trace-picture-warning" className="text-[11px] px-2 py-0.5 rounded bg-amber-100 text-amber-900 font-semibold">
              Picture template — only what you draw is saved. Trace the lines, not just pins.
            </span>
          )}
          <ConnectionSummary summary={connections} />

          {/* Default type — ALWAYS visible. Filters based on current
              geometry mode so Line modes show line types, Point mode
              shows point types. */}
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 ml-1" title="The type new shapes will inherit. Pick BEFORE drawing.">
            <span className="font-bold uppercase tracking-wider text-[9px] text-slate-500">Drawing as</span>
            <span
              data-testid="trace-default-type-swatch"
              className="inline-block w-3.5 h-3.5 rounded-sm ring-1 ring-slate-300"
              style={{ background: typeColour(mode === 'point' ? defaultPointType : defaultLineType) }}
            />
            <select
              data-testid="trace-default-type"
              value={mode === 'point' ? defaultPointType : defaultLineType}
              onChange={(e) => mode === 'point' ? setDefaultPointType(e.target.value) : setDefaultLineType(e.target.value)}
              className="px-2 py-1 border border-slate-300 rounded text-[11px] font-bold cursor-pointer"
              style={{ color: typeColour(mode === 'point' ? defaultPointType : defaultLineType), background: '#FFFFFF' }}
            >
              {(mode === 'point' ? POINT_LINE_TYPES : LINE_LINE_TYPES).map(t => (
                <option key={t.key} value={t.key} style={{ color: t.colour, fontWeight: 700 }}>{t.label}</option>
              ))}
            </select>
          </label>

          {/* Stroke width */}
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 ml-2">
            Stroke
            <input data-testid="trace-stroke" type="range" min={MIN_STROKE} max={MAX_STROKE} value={stroke}
              onChange={(e) => setStroke(parseInt(e.target.value, 10) || 1)}
              className="w-20 accent-[#D35400]"
            />
            <span className="font-mono w-5 text-right">{stroke}</span>
          </label>

          {/* Snap + H/V lock — bigger / louder when active */}
          <button
            data-testid="trace-snap-toggle"
            onClick={() => setSnapEnabled(v => !v)}
            className={`flex items-center gap-1 text-[11px] px-2.5 py-1 rounded border font-bold ${snapEnabled ? 'border-emerald-600 bg-emerald-500 text-white' : 'border-slate-300 bg-white text-slate-500'}`}
            title="Snap new clicks to existing endpoints so lines join cleanly."
          ><Magnet size={11} /> Snap {snapEnabled && <span className="text-[9px] opacity-80 ml-0.5">ON</span>}</button>
          <button
            data-testid="trace-hv-toggle"
            onClick={() => setHvLock(v => !v)}
            className={`flex items-center gap-1 text-[11px] px-2.5 py-1 rounded border font-bold ${(hvLock || shiftDown) ? 'border-sky-600 bg-sky-500 text-white' : 'border-slate-300 bg-white text-slate-500'}`}
            title="Lock new lines to perfectly horizontal or vertical. Hold Shift for temporary lock."
          ><RectangleHorizontal size={11} /> H/V {hvLock && <span className="text-[9px] opacity-80 ml-0.5">LOCKED</span>}{shiftDown && !hvLock && <span className="text-[9px] opacity-80 ml-0.5">(Shift)</span>}</button>

          {/* Aerial show/dim */}
          <button
            data-testid="trace-bg-toggle"
            onClick={() => setBgVisible(v => !v)}
            className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-100"
            title="Toggle the aerial background on/off."
          >{bgVisible ? <Eye size={11} /> : <EyeOff size={11} />} Aerial</button>
          {bgVisible && (
            <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
              Dim
              <input data-testid="trace-bg-opacity" type="range" min={20} max={100} step={5} value={bgOpacity}
                onChange={(e) => setBgOpacity(parseInt(e.target.value, 10) || 60)}
                className="w-20 accent-[#D35400]"
              />
              <span className="font-mono w-9 text-right">{bgOpacity}%</span>
            </label>
          )}

          {/* Iter204 — alignment grid overlay. Click to cycle Off →
              Fine → Coarse → Off. Helps the tracer line opposite
              corners and ridges accurately by giving a faded reference
              grid behind the strokes. */}
          <button
            data-testid="trace-grid-toggle"
            onClick={cycleGrid}
            className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded border font-bold ${
              gridMode === 'off'
                ? 'border-slate-300 bg-white text-slate-500'
                : 'border-sky-400 bg-sky-50 text-sky-700'
            }`}
            title={
              gridMode === 'off' ? 'Click to show a faded alignment grid (20px). Click again for a coarser grid.' :
              gridMode === 'fine' ? 'Fine grid (20px). Click for a coarser grid (40px).' :
              'Coarse grid (40px). Click to hide.'
            }
          >
            <Grid3x3 size={11} /> Grid
            {gridMode !== 'off' && (
              <span className="text-[9px] uppercase tracking-wider opacity-80 ml-0.5">{gridMode}</span>
            )}
          </button>

          <span className="ml-auto flex items-center gap-2">
            {/* Auto-save status — fades in once the draft has been
                written. Reassures the operator that closing the modal
                won't lose their work. */}
            {draftSavedAt && (
              <span
                data-testid="trace-draft-status"
                className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-1 rounded ring-1 ring-emerald-200"
                title={`Auto-saved draft at ${new Date(draftSavedAt).toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}. Closing the modal won't lose your work — re-open the same aerial and the draft restores automatically.`}
              >
                <Save size={10} /> Draft saved
              </span>
            )}
            <button
              data-testid="trace-undo"
              onClick={undo}
              disabled={strokes.length === 0 && (!activePoly || activePoly.points.length === 0) && !activeLineStart}
              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40"
              title="Undo last point / stroke (Ctrl+Z). Backspace works mid-polygon."
            ><Undo2 size={11} /> Undo</button>
            <button
              data-testid="trace-redo"
              onClick={redo}
              disabled={redoStack.length === 0}
              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-100 disabled:opacity-40"
              title="Redo last undone stroke (Ctrl+Y / Ctrl+Shift+Z)."
            ><Redo2 size={11} /> Redo{redoStack.length > 1 && <span className="ml-0.5 text-[9px] opacity-60">×{redoStack.length}</span>}</button>
            <button
              data-testid="trace-discard-draft"
              onClick={discardDraft}
              disabled={strokes.length === 0 && !draftSavedAt}
              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-amber-300 bg-white text-amber-700 hover:bg-amber-50 disabled:opacity-40"
              title="Discard the auto-saved draft and start fresh."
            ><Trash2 size={11} /> Discard draft</button>
            <button
              data-testid="trace-clear"
              onClick={clearAll}
              disabled={strokes.length === 0 && !activePoly && !activeLineStart}
              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-rose-300 bg-white text-rose-600 hover:bg-rose-50 disabled:opacity-40"
              title="Wipe ALL traced strokes (does NOT touch the saved draft — use Discard for that)."
            ><Trash2 size={11} /> Clear</button>
            {/* Iter182++ — Auto-arrange labels. Runs the perpendicular
                placement + overlap-relaxation algorithm so every chip
                ends up just off its line and never overlaps a sibling.
                Cheap operator escape hatch when the canvas gets busy. */}
            <button
              data-testid="trace-auto-arrange"
              onClick={autoArrangeLabels}
              disabled={strokes.length === 0}
              className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-sky-300 bg-white text-sky-700 hover:bg-sky-50 disabled:opacity-40"
              title="Auto-shift every pin tag just off its line and pull them apart so nothing overlaps."
            ><Magnet size={11} /> Auto-arrange</button>
          </span>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 bg-slate-50 relative">
          {error && (
            <div data-testid="trace-error" className="mb-2 px-3 py-2 rounded text-xs font-semibold bg-rose-50 text-rose-700 ring-1 ring-rose-200 flex items-center gap-1.5">
              <AlertTriangle size={12} /> {error}
            </div>
          )}
          {savedFlash?.ok && (
            <div data-testid="trace-saved-flash" className="mb-2 px-3 py-2 rounded text-xs font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
              {savedFlash.msg}
            </div>
          )}

          <div
            ref={wrapRef}
            data-testid="trace-canvas-wrap"
            className="relative inline-block max-w-full select-none"
            style={{ cursor: editMode ? 'pointer' : (copyGhost ? 'copy' : 'crosshair') }}
            onMouseMove={onPointerMove}
            onMouseLeave={() => setSnapHit(null)}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
            onContextMenu={(e) => {
              // Iter238 — right-click on an existing stroke starts the
              // copy-ghost flow. Right-click while a ghost is already
              // active cancels it. Right-click on empty space is a
              // no-op. We suppress the browser context menu in all
              // cases inside the trace canvas.
              e.preventDefault();
              if (copyGhost) { setCopyGhost(null); return; }
              const { pt } = adjust(rawFromClient(e));
              const idx = hitTestShape(pt);
              if (idx !== null) startCopyGhost(idx);
            }}
          >
            <img
              ref={imgRef}
              src={imageUrl}
              alt={aerial.name || 'aerial'}
              onLoad={onImgLoad}
              draggable={false}
              className="max-w-full max-h-[70vh] block border border-slate-300 rounded"
              style={{
                opacity: bgVisible ? bgOpacity / 100 : 0,
                background: bgVisible ? 'transparent' : '#FFFFFF',
                transition: 'opacity 0.15s ease',
              }}
            />
            {boxDims.w > 0 && (
              <svg
                data-testid="trace-svg"
                className="absolute inset-0 pointer-events-none"
                width={boxDims.w}
                height={boxDims.h}
              >
                {/* Reusable arrow-head markers (one per typed colour
                    that uses arrows). Face = double-arrow dimension
                    line — markers attached at both endpoints. */}
                <defs>
                  <marker id="trace-arrow-face" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill={typeColour('face')} />
                  </marker>
                  {/* Iter204 — faded alignment grid pattern. Two layers:
                      a thin sub-grid every 1/5th of the major step
                      (extra-faint) and the main grid at gridStep. */}
                  {gridStep > 0 && (
                    <>
                      <pattern id="trace-grid-fine" width={gridStep / 5} height={gridStep / 5} patternUnits="userSpaceOnUse">
                        <path d={`M ${gridStep / 5} 0 L 0 0 0 ${gridStep / 5}`} fill="none" stroke="#94a3b8" strokeOpacity="0.18" strokeWidth="0.5" />
                      </pattern>
                      <pattern id="trace-grid-major" width={gridStep} height={gridStep} patternUnits="userSpaceOnUse">
                        <rect width={gridStep} height={gridStep} fill="url(#trace-grid-fine)" />
                        <path d={`M ${gridStep} 0 L 0 0 0 ${gridStep}`} fill="none" stroke="#64748b" strokeOpacity="0.35" strokeWidth="0.8" />
                      </pattern>
                    </>
                  )}
                </defs>
                {/* Iter204 — render the grid behind every stroke so it
                    doesn't obscure the operator's work. Pointer-events
                    stay off so clicks still hit the underlying aerial. */}
                {gridStep > 0 && (
                  <rect
                    data-testid="trace-grid-rect"
                    x={0} y={0}
                    width={boxDims.w} height={boxDims.h}
                    fill="url(#trace-grid-major)"
                    pointerEvents="none"
                  />
                )}
                {/* Committed strokes */}
                {strokes.map((s, i) => {
                  const dim = editingIdx !== null && i !== editingIdx ? 0.35 : 1;
                  const colour = typeColour(s.lineType);
                  // Point feature → circle (Outlet / Flue / Pipe)
                  if (s.type === 'point' && s.points?.length > 0) {
                    const r = pointRadiusIntrinsic(s) * scale;
                    const code = pinCodeForIdx(strokes, i);
                    const isAreaPt = typeMeasure(s.lineType) === 'area';
                    const hoverTitle = `${code} · ${typeLabel(s.lineType)}${isAreaPt ? (s.area_m2 ? ` · ${s.area_m2} m²` : ' · (click to set m²)') : s.diameter_mm ? ' · DN' + s.diameter_mm : ' · (click to set diameter)'}`;
                    return (
                      <g key={i} opacity={dim}
                         style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                         onClick={(e) => { e.stopPropagation(); if (justDraggedRef.current) { justDraggedRef.current = false; return; } setEditingIdx(i); }}
                         onDoubleClick={(e) => e.stopPropagation()}>
                        <title>{hoverTitle}</title>
                        <circle
                          cx={s.points[0].x * scale}
                          cy={s.points[0].y * scale}
                          r={r}
                          fill="#FFFFFF"
                          stroke={colour}
                          strokeWidth={(s.stroke || stroke) + (i === editingIdx ? 2 : 0)}
                        />
                        {s.diameter_mm || s.area_m2 ? (
                          <text
                            x={s.points[0].x * scale}
                            y={s.points[0].y * scale + r + 10}
                            textAnchor="middle"
                            fontSize={s.area_m2 ? 12 : 9}
                            fontFamily="monospace"
                            fontWeight="700"
                            fill={colour}
                          >{s.area_m2 ? `${Number(s.area_m2).toFixed(1)} m²` : `DN${s.diameter_mm}`}</text>
                        ) : null}
                        <text
                          x={s.points[0].x * scale}
                          y={s.points[0].y * scale + 3}
                          textAnchor="middle"
                          fontSize={9}
                          fontWeight="800"
                          fill={colour}
                        >{code}</text>
                      </g>
                    );
                  }
                  // Linear feature
                  return (
                    <g key={i} opacity={dim}>
                      <path
                        d={path(s.points, s.type === 'polygon' && s.closed)}
                        fill="none"
                        stroke={colour}
                        strokeWidth={(s.stroke || stroke) + (i === editingIdx ? 2 : 0)}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        strokeDasharray={typeDash(s.lineType) || undefined}
                        markerStart={typeArrows(s.lineType) === 'double' ? 'url(#trace-arrow-face)' : undefined}
                        markerEnd={typeArrows(s.lineType) === 'double' ? 'url(#trace-arrow-face)' : undefined}
                      />
                      {/* Iter182++ — Pin-code + measurement badge.
                          The badge sits at the line's midpoint plus an
                          operator-draggable offset (label_dx, label_dy in
                          intrinsic coords). A dotted leader line ties the
                          badge back to the true midpoint so the link is
                          obvious when dragged away. Click without drag →
                          opens the line editor. Click + drag → moves the
                          tag only; the underlying line is never touched. */}
                      {(() => {
                        const m = midpoint(s.points);
                        if (!m) return null;
                        const isArea = typeMeasure(s.lineType) === 'area';
                        const v = isArea ? s.area_m2 : s.length_m;
                        const code = pinCodeForIdx(strokes, i);
                        const vTxt = v ? (isArea ? `${Number(v).toFixed(1)} m²` : `${Number(v).toFixed(2)} m`) : '';
                        const txt = vTxt ? `${code} · ${vTxt}` : code;
                        const w   = Math.max(28, txt.length * 6.2) + 8;
                        const hoverTitle = `${code} · ${typeLabel(s.lineType)}${vTxt ? ' · ' + vTxt : ' · (click to set length · drag to move tag)'}`;
                        const dx = s.label_dx || 0;
                        const dy = s.label_dy || 0;
                        const showLeader = Math.hypot(dx, dy) > 2 / scale;
                        return (
                          <g>
                            {showLeader && (
                              <line
                                x1={m.x * scale} y1={m.y * scale}
                                x2={(m.x + dx) * scale} y2={(m.y + dy) * scale}
                                stroke={colour} strokeWidth={1}
                                strokeDasharray="3 3" opacity={0.7}
                              />
                            )}
                            <g
                              transform={`translate(${(m.x + dx) * scale} ${(m.y + dy) * scale})`}
                              style={{ cursor: 'grab', pointerEvents: 'auto' }}
                              onClick={(e) => e.stopPropagation()}
                              onDoubleClick={(e) => e.stopPropagation()}
                              onPointerDown={(e) => {
                                e.stopPropagation();
                                if (saving || editingIdx !== null) return;
                                const startX = e.clientX, startY = e.clientY;
                                const baseDx = s.label_dx || 0;
                                const baseDy = s.label_dy || 0;
                                let didMove = false;
                                const onMove = (ev) => {
                                  const ddx = (ev.clientX - startX) / scale;
                                  const ddy = (ev.clientY - startY) / scale;
                                  if (!didMove && Math.hypot(ev.clientX - startX, ev.clientY - startY) > 3) {
                                    didMove = true;
                                  }
                                  if (didMove) {
                                    setStrokes(arr => arr.map((x, ix) => ix === i ? { ...x, label_dx: baseDx + ddx, label_dy: baseDy + ddy } : x));
                                  }
                                };
                                const onUp = () => {
                                  document.removeEventListener('pointermove', onMove);
                                  document.removeEventListener('pointerup', onUp);
                                  if (!didMove) {
                                    // No drag → treat as click → open editor.
                                    setEditingIdx(i);
                                  }
                                };
                                document.addEventListener('pointermove', onMove);
                                document.addEventListener('pointerup', onUp);
                              }}
                            >
                              <title>{hoverTitle}</title>
                              <rect x={-w/2} y={-9} width={w} height={17} rx={3.5}
                                    fill={v ? '#FFFFFF' : colour}
                                    stroke={colour} strokeWidth={1.2} opacity={0.97} />
                              <text x={0} y={3} textAnchor="middle" fontSize={10}
                                    fontFamily="ui-sans-serif, system-ui, sans-serif"
                                    fill={v ? colour : '#FFFFFF'} fontWeight="800">{txt}</text>
                            </g>
                          </g>
                        );
                      })()}
                    </g>
                  );
                })}

                {/* In-flight straight-line: anchor + rubber-band */}
                {activeLineStart && (
                  <>
                    <circle cx={activeLineStart.x * scale} cy={activeLineStart.y * scale} r={5} fill="#15803D" stroke="#FFFFFF" strokeWidth={1.2} />
                    {cursor && (
                      <line
                        x1={activeLineStart.x * scale} y1={activeLineStart.y * scale}
                        x2={cursor.x * scale}          y2={cursor.y * scale}
                        stroke={typeColour(defaultLineType)} strokeDasharray="4 4" strokeWidth={stroke}
                      />
                    )}
                  </>
                )}
                {/* In-flight point preview — ghost circle following the cursor */}
                {mode === 'point' && !editMode && cursor && (
                  <circle
                    cx={cursor.x * scale}
                    cy={cursor.y * scale}
                    r={14 * scale}
                    fill="none"
                    stroke={typeColour(defaultPointType)}
                    strokeWidth={stroke}
                    strokeDasharray="3 3"
                    opacity={0.6}
                  />
                )}
                {/* In-flight polygon */}
                {activePoly && (
                  <>
                    <path d={path(activePoly.points, false)} fill="none" stroke="#D35400" strokeWidth={stroke} strokeLinejoin="round" />
                    {cursor && (
                      <line
                        x1={(activePoly.points[activePoly.points.length - 1]?.x || 0) * scale}
                        y1={(activePoly.points[activePoly.points.length - 1]?.y || 0) * scale}
                        x2={cursor.x * scale} y2={cursor.y * scale}
                        stroke="#D35400" strokeDasharray="4 4" strokeWidth={stroke}
                      />
                    )}
                    {activePoly.points.map((p, i) => (
                      <circle key={i} cx={p.x * scale} cy={p.y * scale} r={i === 0 ? 5 : 3}
                        fill={i === 0 ? '#15803D' : '#D35400'} stroke="#FFFFFF" strokeWidth={1.2} />
                    ))}
                  </>
                )}

                {/* Snap indicator — highlight the magnet target */}
                {strokes.length > 0 && (
                  <EndpointLayer nodes={connections.nodes} strokes={strokes} scale={scale}
                    editMode={editMode} onStartDrag={startEndpointDrag} />
                )}
                {snapHit && (
                  <circle
                    cx={snapHit.x * scale} cy={snapHit.y * scale}
                    r={8} fill="none" stroke="#10B981" strokeWidth={2}
                  />
                )}

                {/* Iter238 — ghost preview of a line being copy/pasted.
                    Translucent dashed copy of the source stroke follows
                    the cursor. The anchor endpoint is highlighted with
                    a magnet ring so the operator knows which end is
                    locked to the cursor (Tab swaps it). */}
                {copyGhost && cursor && (() => {
                  const gPts = computeGhostPoints(copyGhost, cursor);
                  if (!gPts || gPts.length === 0) return null;
                  const ghostStroke = typeColour(copyGhost.src.lineType || defaultLineType);
                  if (copyGhost.src.type === 'point') {
                    return (
                      <circle
                        data-testid="trace-copy-ghost-point"
                        cx={gPts[0].x * scale} cy={gPts[0].y * scale}
                        r={14 * scale}
                        fill="none" stroke={ghostStroke}
                        strokeWidth={stroke} strokeDasharray="3 3" opacity={0.6}
                      />
                    );
                  }
                  return (
                    <g data-testid="trace-copy-ghost-line">
                      <path
                        d={path(gPts, !!copyGhost.src.closed)}
                        fill="none"
                        stroke={ghostStroke}
                        strokeWidth={stroke + 0.5}
                        strokeDasharray="6 4"
                        opacity={0.7}
                        strokeLinejoin="round"
                      />
                      {/* Anchor endpoint magnet ring */}
                      <circle
                        cx={gPts[copyGhost.anchorIdx]?.x * scale}
                        cy={gPts[copyGhost.anchorIdx]?.y * scale}
                        r={7} fill="#10B981" stroke="#FFFFFF" strokeWidth={1.5}
                      />
                    </g>
                  );
                })()}

                {/* H/V cursor badge — pops up next to the cursor when
                    a lock is active AND we have an anchor (in-flight). */}
                {(hvLock || shiftDown) && cursor && anchorPoint() && (() => {
                  const a = anchorPoint();
                  const orient = Math.abs(cursor.x - a.x) >= Math.abs(cursor.y - a.y) ? 'H' : 'V';
                  return (
                    <g transform={`translate(${cursor.x * scale + 12} ${cursor.y * scale - 12})`}>
                      <rect x={-1} y={-9} width={20} height={16} rx={3} fill="#0EA5E9" />
                      <text x={9} y={2} textAnchor="middle" fontSize={10} fontWeight={700} fill="#FFFFFF">{orient}</text>
                    </g>
                  );
                })()}
              </svg>
            )}

            {/* Inline editor (HTML — floats over the canvas at the
                target shape's midpoint). */}
            {editingShape && editingAnchor && (() => {
              const isPoint    = editingShape.type === 'point';
              const lt         = _t(editingShape.lineType);
              const typeOptions = isPoint ? POINT_LINE_TYPES : LINE_LINE_TYPES;
              const measureKind = typeMeasure(editingShape.lineType);  // 'area' | 'length'
              const flueTypes  = lt.flueTypes || null;
              const diameters  = lt.diameters || null;
              return (
              <div
                data-testid="trace-edit-popover"
                className="absolute bg-white border-2 rounded-md shadow-lg p-2.5 z-10 pointer-events-auto"
                style={{
                  left:  Math.max(8, Math.min(boxDims.w - 250, editingAnchor.x * scale - 110)),
                  top:   Math.max(8, Math.min(boxDims.h - 160, editingAnchor.y * scale - 70)),
                  borderColor: typeColour(editingShape.lineType),
                  width: 240,
                }}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    {isPoint ? 'Edit point' : 'Edit line'}
                  </span>
                  <button onClick={() => setEditingIdx(null)} className="p-0.5 rounded hover:bg-slate-100"><X size={12} /></button>
                </div>
                <label className="block text-[10px] font-semibold text-slate-500 mb-0.5">Type</label>
                <select
                  data-testid="trace-edit-type"
                  value={editingShape.lineType}
                  onChange={(e) => patchShape(editingIdx, { lineType: e.target.value })}
                  className="w-full mb-1.5 px-1.5 py-1 border border-slate-300 rounded text-[11px] font-semibold"
                  style={{ color: typeColour(editingShape.lineType) }}
                >
                  {typeOptions.map(t => <option key={t.key} value={t.key} style={{ color: t.colour }}>{t.label}</option>)}
                </select>

                {/* Flue sub-type dropdown — only for flue */}
                {isPoint && flueTypes && (
                  <>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-0.5">Flue type</label>
                    <select
                      data-testid="trace-edit-flue-type"
                      value={editingShape.flue_type || flueTypes[0]}
                      onChange={(e) => patchShape(editingIdx, { flue_type: e.target.value })}
                      className="w-full mb-1.5 px-1.5 py-1 border border-slate-300 rounded text-[11px]"
                    >
                      {flueTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </>
                )}

                {isPoint && measureKind !== 'area' ? (
                  <>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-0.5">Diameter (mm)</label>
                    {diameters ? (
                      <select
                        data-testid="trace-edit-diameter"
                        value={editingShape.diameter_mm || ''}
                        onChange={(e) => patchShape(editingIdx, { diameter_mm: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                        onKeyDown={(e) => { if (e.key === 'Enter') setEditingIdx(null); }}
                        autoFocus
                        className="w-full mb-2 px-1.5 py-1 border border-slate-300 rounded text-[11px] font-mono"
                      >
                        <option value="">— pick diameter —</option>
                        {diameters.map(d => <option key={d} value={d}>DN{d}</option>)}
                      </select>
                    ) : (
                      <input
                        data-testid="trace-edit-diameter"
                        type="number" step="5" min="0"
                        value={editingShape.diameter_mm ?? ''}
                        onChange={(e) => patchShape(editingIdx, { diameter_mm: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                        onKeyDown={(e) => { if (e.key === 'Enter') setEditingIdx(null); }}
                        placeholder="e.g. 80, 100, 150"
                        autoFocus
                        className="w-full mb-2 px-1.5 py-1 border border-slate-300 rounded text-[11px] font-mono"
                      />
                    )}
                  </>
                ) : measureKind === 'area' ? (
                  <>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-0.5">Area (m²)</label>
                    <input
                      data-testid="trace-edit-area"
                      type="number" step="0.1" min="0"
                      value={editingShape.area_m2 ?? ''}
                      onChange={(e) => patchShape(editingIdx, { area_m2: e.target.value === '' ? null : parseFloat(e.target.value) })}
                      onKeyDown={(e) => { if (e.key === 'Enter') setEditingIdx(null); }}
                      placeholder="e.g. 24.5"
                      autoFocus
                      className="w-full mb-2 px-1.5 py-1 border border-slate-300 rounded text-[11px] font-mono"
                    />
                  </>
                ) : (
                  <>
                    <label className="block text-[10px] font-semibold text-slate-500 mb-0.5">Length (m)</label>
                    <input
                      data-testid="trace-edit-length"
                      type="number" step="0.01" min="0"
                      value={editingShape.length_m ?? ''}
                      onChange={(e) => patchShape(editingIdx, { length_m: e.target.value === '' ? null : parseFloat(e.target.value) })}
                      onKeyDown={(e) => { if (e.key === 'Enter') setEditingIdx(null); }}
                      placeholder="optional, e.g. 8.4"
                      autoFocus
                      className="w-full mb-2 px-1.5 py-1 border border-slate-300 rounded text-[11px] font-mono"
                    />
                  </>
                )}
                {/* Sprint 1 (2026-02c) — on_perimeter flag for Spouting
                    (gutter) and Barge lines. Both can be interior
                    features (box gutter between roof faces · gable
                    barge inside the roof outline) OR sit on the
                    boundary. Flag drives the composed-perimeter
                    resolvers in Sprint 2. */}
                {!isPoint && (editingShape.lineType === 'gutter' || editingShape.lineType === 'barge') && (
                  <label className="flex items-center gap-1.5 mb-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      data-testid="trace-edit-on-perimeter"
                      checked={editingShape.on_perimeter ?? (editingShape.lineType === 'gutter')}
                      onChange={(e) => patchShape(editingIdx, { on_perimeter: e.target.checked })}
                      className="h-3 w-3"
                    />
                    <span className="text-[10px] font-semibold text-slate-600">
                      On perimeter (boundary line)
                    </span>
                  </label>
                )}
                <div className="flex items-center justify-between gap-1">
                  <button
                    data-testid="trace-edit-delete"
                    onClick={() => { removeShape(editingIdx); setEditingIdx(null); }}
                    className="flex items-center gap-1 text-[10px] px-1.5 py-1 rounded border border-rose-300 text-rose-600 hover:bg-rose-50"
                  ><Trash2 size={10} /> Delete</button>
                  <button
                    data-testid="trace-edit-copy"
                    onClick={() => startCopyGhost(editingIdx)}
                    className="flex items-center gap-1 text-[10px] px-1.5 py-1 rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50 font-semibold"
                    title="Copy this line — a ghost duplicate will follow your cursor. R = rotate 15°, Tab = swap anchor, Esc = cancel."
                  >📋 Copy line</button>
                  <button
                    onClick={() => setEditingIdx(null)}
                    className="text-[10px] px-2 py-1 rounded bg-[#D35400] text-white font-bold hover:bg-[#A04000]"
                  >Done</button>
                </div>
              </div>
              );
            })()}
          </div>

          <p className="mt-2 text-[11px] text-slate-500 leading-relaxed">
            <strong>⚠ Pick "Drawing as" FIRST</strong> — it tags every new shape with that type / colour.{' '}
            <strong>Polygon:</strong> click corners — <kbd className="px-1 py-0.5 bg-slate-200 rounded text-[10px]">Enter</kbd> to close.{' '}
            <strong>Straight line:</strong> click start, click end. Click on an existing endpoint to chain.{' '}
            <strong>Point:</strong> click drops a circle — editor asks diameter (Face m²: asks area).{' '}
            <strong>Double-click</strong> a line to edit type / length.{' '}
            <strong>H/V</strong> (or <kbd className="px-1 py-0.5 bg-slate-200 rounded text-[10px]">Shift</kbd>) locks horizontal / vertical.{' '}
            <strong>Right-click</strong> a line to copy/paste it — drag the ghost to its new spot,{' '}
            <kbd className="px-1 py-0.5 bg-slate-200 rounded text-[10px]">R</kbd> rotates 15°,{' '}
            <kbd className="px-1 py-0.5 bg-slate-200 rounded text-[10px]">Tab</kbd> swaps anchor,{' '}
            <kbd className="px-1 py-0.5 bg-slate-200 rounded text-[10px]">Esc</kbd> cancels.
          </p>
          {/* Iter238 — ghost-mode banner. Surfaces the current copy state
              and exposes the same keybindings inline. */}
          {copyGhost && (
            <div
              data-testid="trace-copy-ghost-banner"
              className="mt-2 px-3 py-2 rounded bg-indigo-50 ring-1 ring-indigo-300 text-[11px] text-indigo-900 flex items-center justify-between gap-3"
            >
              <span>
                <strong>📋 Copying {copyGhost.src.type === 'point' ? 'point' : 'line'}</strong>
                {' '}— click to place · rotation <strong>{copyGhost.rotDeg || 0}°</strong>
              </span>
              <span className="flex items-center gap-2 text-[10px]">
                <kbd className="px-1 py-0.5 bg-white ring-1 ring-indigo-200 rounded">R</kbd> rotate
                <kbd className="px-1 py-0.5 bg-white ring-1 ring-indigo-200 rounded">Tab</kbd> swap anchor
                <kbd className="px-1 py-0.5 bg-white ring-1 ring-indigo-200 rounded">Esc</kbd> cancel
                <button
                  data-testid="trace-copy-ghost-cancel"
                  onClick={() => setCopyGhost(null)}
                  className="ml-2 px-2 py-0.5 text-[10px] font-semibold rounded bg-white text-indigo-700 ring-1 ring-indigo-300 hover:bg-indigo-100"
                >Cancel</button>
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-slate-200 bg-white flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-xs flex-1 min-w-[240px]">
            <span className="text-slate-500 font-semibold uppercase tracking-wider text-[10px]">Name</span>
            <input
              data-testid="trace-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. 5 Hip 1 Valley — traced from 12 Smith St aerial"
              className="flex-1 px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-[#D35400]"
            />
          </label>
          {/* Per-slot assignment — only when launched from an appraisal.
              Each Roof Diagram slot can hold its OWN trade-specific
              drawing rather than all five sharing the same aerial. */}
          {appraisalId && (
            <label className="flex items-center gap-2 text-xs" title="Assign this new template to a specific Roof Diagram slot on this appraisal so each trade sees its own drawing instead of all pointing at the same aerial.">
              <span className="text-slate-500 font-semibold uppercase tracking-wider text-[10px]">Slot</span>
              <select
                data-testid="trace-assign-slot"
                value={assignSlot}
                onChange={(e) => setAssignSlot(e.target.value)}
                className="px-2 py-1.5 border border-slate-300 rounded-md text-xs font-semibold bg-white"
              >
                <option value="">Library only — don't assign</option>
                <option value="roof_plan">Master Roof Plan</option>
                <option value="roofer">Roofing</option>
                <option value="guttering">Guttering</option>
                <option value="concrete_restore">Concrete Restore</option>
                <option value="painter">Painter</option>
              </select>
              {suggestedSlot && suggestedSlot === assignSlot && (
                <span className="text-[10px] text-emerald-700 font-bold bg-emerald-50 px-1.5 py-0.5 rounded">Auto</span>
              )}
            </label>
          )}
          <span className="text-[11px] text-slate-400">{strokes.length} shape{strokes.length === 1 ? '' : 's'} drawn</span>
          {appraisalId && (
            <button
              data-testid="trace-save-all-slots-btn"
              onClick={saveToAllSlots}
              disabled={saving || strokes.length === 0 || !name.trim()}
              title="Generate 5 trade-specific filtered templates from this tracing and assign each to its Roof Diagram slot in one click. Aerial is NOT baked into any of them."
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-bold border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Save → all slots
            </button>
          )}
          {/* Iter182++ — Save as this Property's main template. One-click
              upload + link the new template as the property's
              roof_template_id, so every appraisal for this property
              pre-fills with this template by default.
              Iter205 — if the operator already clicked "Save as new
              template" in this session, this button just LINKS the
              existing template to the property (no second upload). */}
          {propertyId && (
            <button
              data-testid="trace-save-as-property-btn"
              onClick={saveAsPropertyTemplate}
              disabled={saving || strokes.length === 0 || !name.trim()}
              title={savedTemplateId
                ? 'Link the already-saved template to this property as its main roof template (no duplicate created).'
                : "Save this drawing as the property's main roof template — every future appraisal for this property will pre-fill with it."}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md font-bold border-2 border-sky-600 text-sky-700 hover:bg-sky-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Home size={12} />}
              {savedTemplateId ? 'Link saved template to property' : "Save as this property's template"}
            </button>
          )}
          <button
            data-testid="trace-save-btn"
            onClick={save}
            disabled={saving || strokes.length === 0 || !name.trim() || (!!savedTemplateId && !isEditSave)}
            title={isEditSave ? 'Save your changes back into this template'
              : savedTemplateId
              ? 'Already saved as a template in this session — Undo to make a fresh copy.'
              : (appraisalId ? 'Save to the selected slot' : 'Save as a new roof template in the library')}
            className="flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-md font-bold bg-[#D35400] hover:bg-[#A04000] text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {saving ? 'Saving…' : isEditSave ? 'Save changes' : savedTemplateId ? '✓ Saved as template' : (appraisalId ? 'Save to selected slot' : 'Save as new template')}
          </button>
        </div>
      </div>
    </div>
  );
}
